import React, { useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Box,
  Dialog,
  IconButton,
  TextField,
  Toolbar,
  Typography,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import Api from '../../api/userApi';
import SkillLabel from '../Common/SkillLabel';
import VoiceSearchButton from '../Common/VoiceSearchButton';

const ServiceSearchDialog = ({
  open,
  onClose,
  currentService = '',
  village = '',
  staticServices = [],
  minTeamSize = 1,
  onMinTeamSizeChange,
  onServiceSelected
}) => {
  const [searchText, setSearchText] = useState('');
  const [skillsFromApi, setSkillsFromApi] = useState([]);
  const [newSkillName, setNewSkillName] = useState('');
  const [addingSkill, setAddingSkill] = useState(false);

  useEffect(() => {
    if (!open) return;

    let isMounted = true;

    const fetchSkills = async () => {
      try {
        const resp = await axios.get(Api.skills);
        const names = resp.data?.skills || [];
        if (isMounted) {
          setSkillsFromApi(names.map((n) => (typeof n === 'string' ? n : n?.name || '')).filter(Boolean));
        }
      } catch (e) {
        console.error('Failed to load skills for service search:', e);
        if (isMounted) setSkillsFromApi([]);
      }
    };

    fetchSkills();

    return () => {
      isMounted = false;
    };
  }, [open]);

  const allServiceNames = useMemo(() => {
    return Array.from(new Set(skillsFromApi.filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [skillsFromApi]);

  const staticServicesMap = useMemo(() => {
    const map = new Map();
    (staticServices || []).forEach((s) => {
      const name = typeof s === 'string' ? s : s.name;
      if (!name) return;
      map.set(name, s);
    });
    return map;
  }, [staticServices]);

  const filteredServices = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return allServiceNames;
    return allServiceNames.filter((name) =>
      name.toLowerCase().includes(query)
    );
  }, [allServiceNames, searchText]);

  const handleSelect = (value) => {
    if (onServiceSelected) {
      onServiceSelected(value);
    }
    if (onClose) {
      onClose();
    }
  };

  const handleAddNewSkill = async () => {
    const name = (newSkillName || '').toString().trim();
    if (!name) return;
    setAddingSkill(true);
    try {
      const res = await axios.post(Api.skills, { name });
      const updated = res.data?.skills || [];
      if (updated.length) {
        setSkillsFromApi((prev) => Array.from(new Set([...prev, ...updated])).sort((a, b) => a.localeCompare(b)));
        setNewSkillName('');
        handleSelect(name);
      }
    } catch (e) {
      console.error('Failed to add skill:', e);
    } finally {
      setAddingSkill(false);
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { bgcolor: '#f9f5f1' }
      }}
    >
      <AppBar
        elevation={0}
        sx={{
          position: 'sticky',
          top: 0,
          bgcolor: '#221b47'
        }}
      >
        <Toolbar>
          <Typography
            variant="h6"
            sx={{ flex: 1, fontWeight: 600, color: '#fff' }}
          >
            Find your service
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2 }}>
        {/* Workers needed (mobile) */}
        <TextField
          fullWidth
          type="number"
          label="Workers needed"
          value={minTeamSize || 1}
          onChange={(e) => {
            const raw = e.target.value;
            const n = Math.max(1, parseInt(raw, 10) || 1);
            if (typeof onMinTeamSizeChange === 'function') {
              onMinTeamSizeChange(n);
            }
          }}
          inputProps={{ min: 1, step: 1, inputMode: 'numeric' }}
          helperText="Enter 2 or more to include teams/contractors"
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              bgcolor: '#fff',
              borderRadius: 2
            }
          }}
        />

        <TextField
          fullWidth
          autoFocus
          placeholder="Search your service..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: '#6b6b6b' }} />
            ),
            endAdornment: (
              <VoiceSearchButton
                onInterimResult={(text) => setSearchText(text)}
                onFinalResult={(text) => setSearchText(text)}
                iconColor="#221b47"
              />
            )
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              bgcolor: '#fff',
              borderRadius: 2
            }
          }}
        />

        <Typography
          variant="subtitle2"
          sx={{ mb: 1, color: '#7b7b7b', fontWeight: 600 }}
        >
          Popular services
        </Typography>

        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: 2,
            maxHeight: '70vh',
            overflow: 'auto',
            p: 1.5
          }}
        >
          {filteredServices.length > 0 ? (
            <Box
              sx={{
                display: 'grid',
                gap: 1.5,
                // Fully responsive: fills each row with equal-width columns.
                // ~2 columns on very small phones, ~3+ on larger phones/tablets.
                gridTemplateColumns: {
                  xs: 'repeat(auto-fill, minmax(96px, 1fr))',
                  sm: 'repeat(auto-fill, minmax(120px, 1fr))'
                },
                alignItems: 'stretch'
              }}
            >
              {filteredServices.map((name) => {
                const staticService = staticServicesMap.get(name);
                const IconComponent =
                  staticService && typeof staticService !== 'string'
                    ? staticService.icon
                    : null;
                const isSelected = name === currentService;

                return (
                  <Box
                    key={name}
                    onClick={() => handleSelect(name)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: 0.75,
                      py: 1.25,
                      px: 0.75,
                      height: '100%',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: isSelected ? '#221b47' : '#e0e0e0',
                      bgcolor: isSelected ? '#221b47' : '#fafafa',
                      color: isSelected ? '#ffffff' : '#323232',
                      cursor: 'pointer',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                      transition: 'all 0.2s ease',
                      '&:active': {
                        transform: 'scale(0.97)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        flexShrink: 0,
                        borderRadius: '50%',
                        bgcolor: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 3px 8px rgba(0,0,0,0.12)'
                      }}
                    >
                      {IconComponent ? (
                        <IconComponent
                          sx={{
                            fontSize: 28,
                            color: isSelected ? '#221b47' : '#323232'
                          }}
                        />
                      ) : (
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: isSelected ? '#221b47' : '#323232'
                          }}
                        >
                          {name?.[0]?.toUpperCase() || '?'}
                        </Typography>
                      )}
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{
                        textAlign: 'center',
                        fontWeight: 500,
                        lineHeight: 1.2,
                        width: '100%',
                        wordBreak: 'break-word',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      <SkillLabel name={name} />
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ color: '#7b7b7b' }}>
                No services found. Try a different keyword or add your skill below.
              </Typography>
            </Box>
          )}

        <Box sx={{ mt: 2, p: 2, bgcolor: '#f0f0f0', borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: '#555', fontWeight: 600 }}>
            Don&apos;t see your skill?
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Type skill name and click Add"
              value={newSkillName}
              onChange={(e) => setNewSkillName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddNewSkill())}
              sx={{
                flex: '1 1 200px',
                '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 2 }
              }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddNewSkill}
              disabled={addingSkill || !(newSkillName || '').trim()}
            >
              Add
            </Button>
          </Box>
        </Box>
      </Box>
      </Box>
    </Dialog>
  );
};

export default ServiceSearchDialog;


